import { useEffect, useState } from "react";
import { Type } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, DEFAULT_CONTENT } from "../../lib/api";
import { useSite } from "../../lib/SiteContext";

const SHORT_FIELDS = [
  ["site_title", "Site name (logo + browser tab)"],
  ["collection_eyebrow", "Gallery section label"],
  ["collection_title", "Gallery section heading"],
  ["about_eyebrow", "About section label"],
  ["footer_eyebrow", "Footer label"],
];

const HERO_FIELDS = [
  ["hero_eyebrow", "Hero eyebrow line"],
  ["hero_headline_top", "Hero headline — top line"],
  ["hero_headline_bottom", "Hero headline — bottom line (italic gold)"],
];

const CHAPTERS = [1, 2, 3];

export default function SiteTextCard() {
  const { settings, refresh } = useSite();
  const [form, setForm] = useState({ ...DEFAULT_CONTENT, marquee_items: DEFAULT_CONTENT.marquee_items.join(", ") });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const saved = settings?.content || {};
    setForm((f) => ({
      ...f,
      ...saved,
      marquee_items: Array.isArray(saved.marquee_items)
        ? saved.marquee_items.join(", ")
        : f.marquee_items,
    }));
  }, [settings]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const payload = {
        ...form,
        marquee_items: form.marquee_items.split(",").map((s) => s.trim()).filter(Boolean),
      };
      await api.put("/settings/content", payload);
      await refresh();
      toast.success("Site text saved — the gallery now speaks in your words");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="site-text-form" className="border border-line bg-panel p-6 md:p-8">
      <div className="flex items-center gap-3">
        <Type size={14} className="text-gold" />
        <p className="eyebrow !text-ochre/90">Site Words</p>
      </div>
      <h2 className="font-display text-2xl mt-2">Rename anything</h2>
      <p className="text-sm text-mute mt-3 leading-relaxed">
        Every line below is yours to change — the site name, hero words, section
        labels and footer. Leave a field empty to hide that piece of text.
      </p>

      <div className="mt-6 grid md:grid-cols-2 gap-5">
        {SHORT_FIELDS.map(([key, label]) => (
          <div key={key}>
            <label className="field-label" htmlFor={`st-${key}`}>{label}</label>
            <input
              id={`st-${key}`}
              value={form[key] || ""}
              onChange={set(key)}
              className="field-input"
              data-testid={`site-text-${key}-input`}
            />
          </div>
        ))}
      </div>

      <div className="mt-8">
        <p className="eyebrow mb-4">Hero</p>
        <div className="grid md:grid-cols-3 gap-5">
          {HERO_FIELDS.map(([key, label]) => (
            <div key={key}>
              <label className="field-label" htmlFor={`st-${key}`}>{label}</label>
              <input
                id={`st-${key}`}
                value={form[key] || ""}
                onChange={set(key)}
                className="field-input"
                data-testid={`site-text-${key}-input`}
              />
            </div>
          ))}
        </div>
        <div className="mt-5">
          <label className="field-label" htmlFor="st-hero_subline">Hero intro sentence</label>
          <textarea
            id="st-hero_subline"
            rows={2}
            value={form.hero_subline || ""}
            onChange={set("hero_subline")}
            className="field-input resize-y leading-relaxed"
            data-testid="site-text-hero_subline-input"
          />
        </div>
        <div className="mt-5">
          <label className="field-label" htmlFor="st-marquee_items">Scrolling ticker items (separate with commas)</label>
          <input
            id="st-marquee_items"
            value={form.marquee_items || ""}
            onChange={set("marquee_items")}
            placeholder="Original Works, Limited Prints, Enquire Within"
            className="field-input"
            data-testid="site-text-marquee_items-input"
          />
        </div>
      </div>

      <div className="mt-8">
        <p className="eyebrow mb-4">The three manifesto chapters</p>
        <div className="grid md:grid-cols-3 gap-5">
          {CHAPTERS.map((i) => (
            <div key={i} className="space-y-4">
              <div>
                <label className="field-label" htmlFor={`st-chapter_${i}_title`}>Chapter 0{i} — title</label>
                <input
                  id={`st-chapter_${i}_title`}
                  value={form[`chapter_${i}_title`] || ""}
                  onChange={set(`chapter_${i}_title`)}
                  className="field-input"
                  data-testid={`site-text-chapter_${i}_title-input`}
                />
              </div>
              <div>
                <label className="field-label" htmlFor={`st-chapter_${i}_body`}>Chapter 0{i} — text</label>
                <textarea
                  id={`st-chapter_${i}_body`}
                  rows={4}
                  value={form[`chapter_${i}_body`] || ""}
                  onChange={set(`chapter_${i}_body`)}
                  className="field-input resize-y leading-relaxed"
                  data-testid={`site-text-chapter_${i}_body-input`}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8 grid md:grid-cols-2 gap-5">
        {["footer_eyebrow", "footer_title"].map((key) => (
          <div key={key}>
            <label className="field-label" htmlFor={`st-${key}`}>
              {key === "footer_eyebrow" ? "Footer label" : "Footer heading"}
            </label>
            <input
              id={`st-${key}`}
              value={form[key] || ""}
              onChange={set(key)}
              className="field-input"
              data-testid={`site-text-${key}-input`}
            />
          </div>
        ))}
      </div>

      <button
        type="submit"
        disabled={busy}
        data-testid="site-text-save-button"
        className="mt-8 w-full border border-gold/60 bg-gold text-ink font-meta text-[11px] uppercase tracking-[0.3em] py-4 hover:bg-transparent hover:text-gold transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {busy ? "Saving…" : "Save Site Text"}
      </button>
    </form>
  );
}
